import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useState, type ReactNode } from 'react';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import type { Role } from '@/lib/types';
import { NotificationBell } from './NotificationBell';
import { ThemeToggle } from './ThemeToggle';
import { CommandPalette, useCommandPaletteShortcut } from './CommandPalette';

interface NavItem {
    href: string;
    label: string;
    icon?: string;
}

const NAV_BY_ROLE: Record<Role, NavItem[]> = {
    estudiante: [
        { href: '/estudiante/dashboard', label: 'Dashboard', icon: '🎓' },
        { href: '/estudiante/portafolio', label: 'Portafolio', icon: '📁' },
        { href: '/estudiante/mi-ruta', label: 'Mi Ruta', icon: '🗺️' },
        { href: '/estudiante/bitacora', label: 'Bitácora', icon: '📔' },
        { href: '/estudiante/datos-personales', label: 'Datos', icon: '🔐' },
        { href: '/estudiante/perfil', label: 'Mi Perfil', icon: '👤' },
    ],
    profesor: [
        { href: '/profesor/dashboard', label: 'Dashboard', icon: '📊' },
        { href: '/profesor/cursos', label: 'Cursos', icon: '📚' },
        { href: '/profesor/alumnos', label: 'Alumnos', icon: '👥' },
        { href: '/profesor/en-practica', label: 'En práctica', icon: '🏭' },
        { href: '/profesor/observaciones', label: 'Observaciones', icon: '📝' },
        { href: '/profesor/logros', label: 'Logros', icon: '🏆' },
        { href: '/profesor/perfil', label: 'Mi Perfil', icon: '👤' },
    ],
    maestro_guia: [
        { href: '/maestro-guia/dashboard', label: 'Dashboard', icon: '🏭' },
        { href: '/maestro-guia/asistencia', label: 'Asistencia', icon: '📅' },
        { href: '/maestro-guia/bitacora', label: 'Bitácora', icon: '📔' },
        { href: '/maestro-guia/avance', label: 'Avance', icon: '📈' },
        { href: '/maestro-guia/observacion', label: 'Observación', icon: '📝' },
        { href: '/maestro-guia/evaluacion', label: 'Evaluación', icon: '📋' },
        { href: '/maestro-guia/logros', label: 'Logros', icon: '🏆' },
        { href: '/maestro-guia/contacto', label: 'Contacto', icon: '📞' },
        { href: '/maestro-guia/perfil', label: 'Mi Perfil', icon: '👤' },
    ],
    administrador: [
        { href: '/admin/dashboard', label: 'Dashboard', icon: '⚙️' },
        { href: '/admin/analytics', label: 'Analytics', icon: '📈' },
        { href: '/admin/usuarios', label: 'Usuarios', icon: '👥' },
        { href: '/admin/invitaciones', label: 'Invitaciones', icon: '✉️' },
        { href: '/admin/cursos', label: 'Cursos', icon: '📚' },
        { href: '/admin/empresas', label: 'Empresas', icon: '🏢' },
        { href: '/admin/rubricas', label: 'Rúbricas', icon: '📋' },
        { href: '/admin/logros', label: 'Logros', icon: '🏆' },
        { href: '/admin/skills', label: 'Competencias', icon: '🎯' },
        { href: '/admin/mfa', label: 'MFA', icon: '🔒' },
        { href: '/admin/perfil', label: 'Mi Perfil', icon: '👤' },
    ],
    super_admin: [
        { href: '/super-admin/dashboard', label: 'Dashboard', icon: '🔱' },
        { href: '/super-admin/liceos', label: 'Liceos', icon: '🏫' },
        { href: '/super-admin/usuarios', label: 'Usuarios', icon: '👥' },
        { href: '/super-admin/errores', label: 'Errores', icon: '🐛' },
    ],
};

const ROLE_BANNER: Record<Role, { icon: string; label: string; color: string }> = {
    estudiante:    { icon: '🎓', label: 'Estudiante',    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
    profesor:      { icon: '📊', label: 'Profesor',      color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
    maestro_guia:  { icon: '🏭', label: 'Maestro Guía',  color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
    administrador: { icon: '⚙️', label: 'Administrador', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' },
    super_admin:   { icon: '🔱', label: 'Super Admin',   color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
};

export function AppShell({ children }: { children: ReactNode }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [paletteOpen, setPaletteOpen] = useState(false);
    useCommandPaletteShortcut(setPaletteOpen);

    if (!user) return null;

    const items = NAV_BY_ROLE[user.role] || [];
    const banner = ROLE_BANNER[user.role];

    async function handleLogout() {
        await logout();
        navigate('/login', { replace: true });
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
            {/* Role banner */}
            <div className={cn('px-4 py-2 text-sm font-medium text-center', banner.color)} role="alert">
                {banner.icon} <strong>Rol Actual:</strong> {banner.label}
            </div>

            {/* Top nav */}
            <nav className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-2">
                            <span className="text-xl font-extrabold text-brand-blue">ConexEd</span>
                        </Link>

                        <div className="hidden md:flex items-center gap-1">
                            {items.map(item => (
                                <NavLink
                                    key={item.href}
                                    to={item.href}
                                    className={({ isActive }) =>
                                        cn(
                                            'px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                                            isActive
                                                ? 'bg-brand-blue/10 text-brand-blue dark:bg-brand-blue/20'
                                                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                        )
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            ))}
                            <button
                                onClick={() => setPaletteOpen(true)}
                                aria-label="Búsqueda (Cmd/Ctrl + K)"
                                title="Cmd/Ctrl + K"
                                className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z" />
                                </svg>
                            </button>
                            <NotificationBell />
                            <ThemeToggle />
                            <button
                                onClick={handleLogout}
                                className="ml-2 px-3 py-2 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                            >
                                Cerrar Sesión
                            </button>
                        </div>

                        <div className="md:hidden flex items-center gap-1">
                            <button
                                onClick={() => setPaletteOpen(true)}
                                aria-label="Búsqueda"
                                className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z" />
                                </svg>
                            </button>
                            <NotificationBell />
                            <ThemeToggle />
                            <details className="relative">
                                <summary className="list-none cursor-pointer p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                    </svg>
                                </summary>
                                <div className="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-30">
                                    {items.map(item => (
                                        <NavLink
                                            key={item.href}
                                            to={item.href}
                                            className={({ isActive }) =>
                                                cn(
                                                    'block px-4 py-2 text-sm',
                                                    isActive
                                                        ? 'bg-brand-blue/10 text-brand-blue'
                                                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                                )
                                            }
                                        >
                                            {item.label}
                                        </NavLink>
                                    ))}
                                    <button
                                        onClick={handleLogout}
                                        className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                                    >
                                        Cerrar Sesión
                                    </button>
                                </div>
                            </details>
                        </div>
                    </div>
                </div>
            </nav>

            <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8" key={location.pathname}>
                {children}
            </main>

            <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />

            <footer className="bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-gray-700 mt-12">
                <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 text-center text-xs text-gray-500 dark:text-gray-400 space-y-2">
                    <p>&copy; 2025-2026 ConexEd · Por Conexium · Todos los derechos reservados.</p>
                    <p className="space-x-3">
                        <Link to="/privacidad" className="hover:text-brand-blue hover:underline">Privacidad</Link>
                        <span>·</span>
                        <Link to="/cookies" className="hover:text-brand-blue hover:underline">Cookies</Link>
                    </p>
                </div>
            </footer>
        </div>
    );
}
